import React, { SyntheticEvent, useCallback, useState } from 'react'
import '@css/pages/community/CommunityTable.scss'
import '@css/pages/community/CommunityMembers.scss'
import '@css/pages/company/CompanyAccountPartners.scss'
import '@css/pages/company/CompanyMemberRow.scss'
import { Link } from 'react-router-dom'
import { Button, Modal } from 'react-bootstrap'
import ToastComponent from '~/common/ToastComponent'
import Globe from '@web/images/community/globe-icon.svg'
import { GetCommunityMemberRowUserDocument, Membership, RemoveMemberDocument } from '~/api/generated/graphql'
import { useCommunity } from '~/contexts/CommunityContext'
import { useAuth } from '~/auth/Auth'
import { useNavigate } from 'react-router'
import { SizeBreakpoint, useWindowSize } from '~/common/hooks/useWindowSize'
import { getAccountPartnerTypeDisplay, getFullName, removeMemberCache } from '~/utils'
import pencilIcon from '@web/images/community/pencil-icon.svg'
import MemberRemove from '@web/images/community/MemberRemove'
import { ProfilePhoto } from '~/common/ProfilePhoto'
import { useClickOnEnter } from '~/common/hooks/useClickOnEnter'
import { elementClicked } from '~/common/EventLogger'
import { useMutation, useQuery } from '@apollo/client'

interface CompanyMemberRowProps {
  member: Membership
  isLeadsList: boolean
  isVeeva: boolean
  setParentModalText?: (s: string) => void
}

export const CompanyMemberRow = ({ member, isLeadsList, isVeeva, setParentModalText }: CompanyMemberRowProps) => {
  const { userId, leader, executiveSponsor } = member

  const { breakpoint } = useWindowSize()
  const { authUserId, isVeevan, profileVisible } = useAuth()
  const { communityId, companyId } = useCommunity()
  const navigate = useNavigate()
  const [isManaging, setIsManaging] = useState<boolean>(false)

  const { data: userData } = useQuery(GetCommunityMemberRowUserDocument, {
    variables: { id: userId ?? '' },
    skip: !userId,
  })

  const communityMember = userData?.user

  const showEditPencil =
    isVeevan &&
    profileVisible &&
    !leader &&
    !executiveSponsor &&
    !isManaging &&
    !isVeeva &&
    !(companyId && companyId === communityMember?.company?.companyId)

  const profileLink = `/profiles/${userId}`

  const showGlobe = member?.accountLead || member?.commercialLead || member?.rdqLead
  const memberTypeDisplay = getAccountPartnerTypeDisplay(
    leader ?? false,
    member?.accountLead ?? false,
    member?.commercialLead ?? false,
    member?.rdqLead ?? false
  )

  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [isHoveringRemove, setIsHoveringRemove] = useState(false)

  const [modalText, setModalText] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const handleHideModal = useCallback(() => {
    setModalText(null)
  }, [])

  const handleEditClick = (e: SyntheticEvent) => {
    setIsManaging(!isManaging)
    elementClicked(e, 'click-community-members-edit', { userId: communityMember?.userId, communityId: communityId })
  }

  const [removeMember] = useMutation(RemoveMemberDocument, {
    update: (cache, { data }) => {
      if (data?.removeMember?.ok) removeMemberCache(cache, data)
    },
  })

  const handleClickRemove = (e: SyntheticEvent) => {
    elementClicked(e, 'click-community-members-deleted', { userId: member?.userId, communityId: communityId })
    setShowRemoveDialog(true)
  }
  const handleCancelRemove = () => setShowRemoveDialog(false)
  const handleConfirmDelete = async () => {
    const response = await removeMember({
      variables: {
        userId: member?.userId || '',
        communityId: communityId ?? '',
      },
    })
    setShowRemoveDialog(false)
    if (authUserId === member?.userId) {
      void navigate(`/companies/${companyId}/home`)
    }
    if (response?.data?.removeMember?.ok) {
      setParentModalText?.(`The member was removed successfully`)
    } else if (response?.data?.removeMember?.error) {
      switch (response.data.removeMember.error.code) {
        case 'cannotRemoveNonVeevan':
          setModalText(`Employees cannot be removed from their customer homepage`)
          break
        default: // fallback just in case
          setModalText('Something went wrong. Please try again.')
          break
      }
    }
  }

  const memberFullName = getFullName(communityMember)
  const memberCompanyName = communityMember?.company?.name
  const memberTitle = communityMember?.title
  const profileTitle = 'View ' + memberFullName + "'s Profile"

  const editRef = useClickOnEnter<HTMLImageElement>()
  const memberInfoRef = useClickOnEnter<HTMLAnchorElement>()

  const nameBadgeLayout = (
    <Link
      ref={memberInfoRef}
      to={profileLink}
      tabIndex={0}
      className={`image-with-details ${isVeeva ? ' full-column' : ' semi-wide-column'}`}
      title={profileTitle}
    >
      <ProfilePhoto userId={userId} />
      <div className="member-container">
        <div className="member-name">{memberFullName}</div>
        <div className="member-title">{memberTitle}</div>
        <div className="member-company">{memberCompanyName}</div>
      </div>
    </Link>
  )

  const modals = (
    <>
      <Modal show={showRemoveDialog} onHide={handleCancelRemove} className={'delete'}>
        <Modal.Header closeButton />
        <Modal.Body>
          <p>Are you sure you want to remove this member from this customer homepage?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={handleCancelRemove}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirmDelete}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!modalText} onHide={handleHideModal}>
        <Modal.Header closeButton>
          <Modal.Body>
            <p>{modalText}</p>
          </Modal.Body>
        </Modal.Header>
      </Modal>
      <ToastComponent show={!!toast} onClose={() => setToast(null)}>
        {toast ?? ''}
      </ToastComponent>
    </>
  )

  const getThirdColumn = () => {
    return (
      <>
        {isLeadsList ? (
          <span className={`row-text company-country-column`} role="country">
            {member.user?.primaryLocationCountry ?? member.user?.secondaryLocationCountry ?? ''}
          </span>
        ) : (
          <span
            className={`row-text company-role-column`}
            role="country"
            dangerouslySetInnerHTML={{
              __html: member.user?.roleAtCompany?.html ?? '',
            }}
          />
        )}
      </>
    )
  }

  if (breakpoint < SizeBreakpoint.lg)
    // mobile layout
    return (
      <div className={'condensed-member'}>
        <div className={'member-card'}>
          {leader && !isVeeva && <span className={'member-leader'}>{memberTypeDisplay}</span>}
          <Link
            className={`condensed-image-with-details member-details`}
            title={profileTitle}
            ref={memberInfoRef}
            to={profileLink}
            tabIndex={-1}
          >
            <ProfilePhoto userId={userId} />
            <div className="member-container">
              <div className="member-name" tabIndex={-1}>
                {memberFullName}
              </div>
              <div className="member-title" tabIndex={-1}>
                {memberTitle}
              </div>
              <div className="member-company" tabIndex={-1}>
                {memberCompanyName}
              </div>
            </div>
          </Link>
          {getThirdColumn()}
          {isManaging && (
            <div className={'button-row bottom-row'}>
              <span className="remove-button" onClick={handleClickRemove} role={'remove-button'} tabIndex={0}>
                <MemberRemove fill={'#c00000'} />
              </span>
              <Button
                variant="light"
                onClick={() => {
                  setIsManaging(!isManaging)
                }}
                role={'cancel-button'}
                tabIndex={0}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
        {showEditPencil && (
          <img
            className="edit-button"
            src={pencilIcon}
            alt="Edit"
            onClick={handleEditClick}
            role={'button'}
            ref={editRef}
            tabIndex={0}
          />
        )}
        {modals}
      </div>
    )
  else
    return (
      <>
        <div className="table-row members company-members">
          {isManaging ? (
            // managing layout
            <>
              {nameBadgeLayout}
              {!isVeeva && (
                <>
                  <div className={'company-type-column'}>
                    <p className={`row-text`} role="type">
                      {memberTypeDisplay}
                    </p>
                  </div>
                </>
              )}
              {getThirdColumn()}
              <div className={`button-row`}>
                <Button
                  variant="light"
                  onClick={() => {
                    setIsManaging(!isManaging)
                  }}
                  role={'cancel-button'}
                  tabIndex={0}
                >
                  Cancel
                </Button>
                <span
                  className="remove-button"
                  onClick={handleClickRemove}
                  onMouseOver={() => setIsHoveringRemove(true)}
                  onMouseOut={() => setIsHoveringRemove(false)}
                  role={'remove-button'}
                  title="Remove member"
                  tabIndex={0}
                >
                  {isHoveringRemove ? <MemberRemove fill={'#c00000'} /> : <MemberRemove fill={'#676969'} />}
                </span>
              </div>
            </>
          ) : (
            // standard layout
            <>
              {/*NAME & JOB TITLE COLUMN*/}
              {nameBadgeLayout}
              {!isVeeva && (
                <>
                  {/*TYPE COLUMN*/}
                  <div className={'leader-type-list company-type-column'}>
                    {showGlobe && <img src={Globe} alt={'Global'} />}
                    <span className={`row-text member-leader${showGlobe ? ' globe' : ' no-globe'}`} role="type">
                      {memberTypeDisplay}
                    </span>
                  </div>
                  {getThirdColumn()}
                </>
              )}
              <div className="edit-actions">
                {showEditPencil && (
                  <img
                    className="edit-button"
                    src={pencilIcon}
                    alt="Edit"
                    onClick={handleEditClick}
                    role={'button'}
                    ref={editRef}
                    tabIndex={0}
                    data-testid={'edit-pencil'}
                  />
                )}
              </div>
            </>
          )}
        </div>
        {modals}
      </>
    )
}
